import React, { useState } from 'react';

import InfoIcon from 'icons/info.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import ErrorMessage from 'components/application/ErrorMessage';
import SidePanel from 'components/application/SidePanel';

import AddButton from 'components/buttons/AddButton';
import SortableCollection from 'components/collections/SortableCollection';
import FormFooter from 'components/sidepanels/FormFooter';

export default function CustomPersonsSidePanel({
  closeSidePanel,
  collection,
  collectionErrors,
  displayValidationErrors,
  isFallback,
  isSidePanelOpen,
  isSubmitDisabled,
  onAdd,
  onCancel,
  onDelete,
  onListInputChange,
  onSave,
  onSortEnd,
  removeErrorStyling,
  resetDefaults,
}) {
  const [scrolledAmount, setScrolledAmount] = useState(0);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'contentRegister.components.customPersons.components.CustomPersonsSidePanel',
  });

  const { t: i18nTCommon } = useTranslation('common');

  return (
    <SidePanel
      bodyContent={
        <>
          <div className='tw-m-5 tw-flex tw-rounded-lg tw-border-0 tw-bg-cyan-025 tw-p-3 tw-text-cyan-800'>
            <div className='tw-mr-3 tw-h-5'>
              <InfoIcon
                className='[&_path]:tw-fill-cyan-800'
                height={20}
                width={20}
              />
            </div>
            <p className='tw-m-0'>{i18nT('description')}</p>
          </div>
          <ErrorMessage
            isFallback={isFallback}
            modifiers='side-panel__alert side-panel__alert--danger'
            validationErrors={displayValidationErrors}
            wrapperClassName='form-container'
          />
          <SortableCollection
            collection={collection}
            collectionErrors={collectionErrors}
            onDelete={onDelete}
            onListInputChange={onListInputChange}
            onSortEnd={onSortEnd}
            removeErrorStyling={removeErrorStyling}
          />
          <div className='side-panel__options-container'>
            <AddButton
              onClick={onAdd}
              text={i18nTCommon('shared.addAnother')}
            />
            <button
              className='app-link reset-defaults-button tw-bg-transparent tw-font-inter tw-text-m tw-font-medium tw-tracking-auto tw-text-blue-500 hover:tw-text-blue-300 active:tw-text-blue-300'
              onClick={resetDefaults}
            >
              {i18nTCommon('shared.resetToDefaultList')}
            </button>
          </div>
        </>
      }
      closeCallback={closeSidePanel}
      color='tertiary'
      contentContext='edit'
      footerContent={
        <FormFooter
          onCancel={onCancel}
          onSubmit={onSave}
          submitButtonDisabled={isSubmitDisabled}
          text={i18nTCommon('shared.saveChanges')}
        />
      }
      headerContent={
        <div className='popup__title popup__title--tertiary'>
          <h1 className='tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
            {i18nTCommon('riskRegister.editPersonsAtRisk')}
          </h1>
        </div>
      }
      isOpen={isSidePanelOpen}
      onBackdropClick={() => {}}
      scrolledAmount={scrolledAmount}
      setScrolledAmount={setScrolledAmount}
      submitDisabled={isSubmitDisabled}
    />
  );
}

CustomPersonsSidePanel.propTypes = {
  closeSidePanel: PropTypes.func.isRequired,
  collection: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      position: PropTypes.number,
    }),
  ).isRequired,
  collectionErrors: PropTypes.exact({
    errorList: PropTypes.arrayOf(
      PropTypes.exact({
        errorMessage: PropTypes.oneOf(['isBlank', 'isDuplicate']).isRequired,
        fieldHighlighted: PropTypes.bool.isRequired,
      }),
    ).isRequired,
    isFallback: PropTypes.bool.isRequired,
  }).isRequired,
  displayValidationErrors: PropTypes.objectOf(
    PropTypes.exact({
      error: PropTypes.exact({ detail: PropTypes.string.isRequired })
        .isRequired,
    }),
  ).isRequired,
  isFallback: PropTypes.bool.isRequired,
  isSidePanelOpen: PropTypes.bool.isRequired,
  isSubmitDisabled: PropTypes.bool.isRequired,
  onAdd: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onListInputChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onSortEnd: PropTypes.func.isRequired,
  removeErrorStyling: PropTypes.func.isRequired,
  resetDefaults: PropTypes.func.isRequired,
};
